/**
 * Context de Cheques
 * Maneja el estado y operaciones relacionadas con cheques
 */
import { createContext, useContext, useReducer, useCallback } from 'react';
import * as chequeService from '../services/chequeService';
import { useNotification } from './NotificationContext';

// Estado inicial
const initialState = {
  cheques: [],
  selectedCheques: [],
  filters: {},
  loading: false,
  error: null,
  currentCheque: null,
};

// Tipos de acciones
const CHEQUE_ACTIONS = {
  FETCH_START: 'FETCH_START',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_ERROR: 'FETCH_ERROR',
  CREATE_SUCCESS: 'CREATE_SUCCESS',
  UPDATE_SUCCESS: 'UPDATE_SUCCESS',
  DELETE_SUCCESS: 'DELETE_SUCCESS',
  SET_CURRENT_CHEQUE: 'SET_CURRENT_CHEQUE',
  SELECT_CHEQUES: 'SELECT_CHEQUES',
  DESELECT_CHEQUES: 'DESELECT_CHEQUES',
  CLEAR_SELECTION: 'CLEAR_SELECTION',
  SET_FILTERS: 'SET_FILTERS',
  CLEAR_FILTERS: 'CLEAR_FILTERS',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Reducer
const chequeReducer = (state, action) => {
  switch (action.type) {
    case CHEQUE_ACTIONS.FETCH_START:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case CHEQUE_ACTIONS.FETCH_SUCCESS:
      return {
        ...state,
        cheques: action.payload,
        loading: false,
        error: null,
      };

    case CHEQUE_ACTIONS.FETCH_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case CHEQUE_ACTIONS.CREATE_SUCCESS:
      return {
        ...state,
        cheques: [...state.cheques, action.payload],
        loading: false,
        error: null,
      };

    case CHEQUE_ACTIONS.UPDATE_SUCCESS:
      return {
        ...state,
        cheques: state.cheques.map((cheque) =>
          cheque.id === action.payload.id ? action.payload : cheque
        ),
        currentCheque:
          state.currentCheque?.id === action.payload.id
            ? action.payload
            : state.currentCheque,
        loading: false,
        error: null,
      };

    case CHEQUE_ACTIONS.DELETE_SUCCESS:
      return {
        ...state,
        cheques: state.cheques.filter(
          (cheque) => cheque.id !== action.payload
        ),
        selectedCheques: state.selectedCheques.filter(
          (id) => id !== action.payload
        ),
        currentCheque:
          state.currentCheque?.id === action.payload
            ? null
            : state.currentCheque,
        loading: false,
        error: null,
      };

    case CHEQUE_ACTIONS.SET_CURRENT_CHEQUE:
      return {
        ...state,
        currentCheque: action.payload,
      };

    case CHEQUE_ACTIONS.SELECT_CHEQUES:
      return {
        ...state,
        selectedCheques: Array.isArray(action.payload)
          ? action.payload
          : [...state.selectedCheques, action.payload],
      };

    case CHEQUE_ACTIONS.DESELECT_CHEQUES:
      return {
        ...state,
        selectedCheques: Array.isArray(action.payload)
          ? state.selectedCheques.filter((id) => !action.payload.includes(id))
          : state.selectedCheques.filter((id) => id !== action.payload),
      };

    case CHEQUE_ACTIONS.CLEAR_SELECTION:
      return {
        ...state,
        selectedCheques: [],
      };

    case CHEQUE_ACTIONS.SET_FILTERS:
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
      };

    case CHEQUE_ACTIONS.CLEAR_FILTERS:
      return {
        ...state,
        filters: {},
      };

    case CHEQUE_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    case CHEQUE_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Crear contexto
const ChequeContext = createContext(undefined);

// Provider
export const ChequeProvider = ({ children }) => {
  const [state, dispatch] = useReducer(chequeReducer, initialState);
  const { showSuccess, showError } = useNotification();

  /**
   * Obtener cheques con filtros
   */
  const fetchCheques = useCallback(
    async (filters = {}) => {
      try {
        dispatch({ type: CHEQUE_ACTIONS.FETCH_START });

        const response = await chequeService.getAllCheques(filters);

        // El servicio ya extrae response.data.data o response.data
        // Ahora necesitamos extraer el array de cheques si viene en un objeto
        const cheques = response.cheques || response || [];

        dispatch({
          type: CHEQUE_ACTIONS.FETCH_SUCCESS,
          payload: cheques,
        });

        return cheques;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Error al obtener cheques';

        dispatch({
          type: CHEQUE_ACTIONS.FETCH_ERROR,
          payload: errorMessage,
        });

        showError(errorMessage);
        throw error;
      }
    },
    [showError]
  );

  /**
   * Crear nuevo cheque
   */
  const createCheque = useCallback(
    async (data) => {
      try {
        dispatch({ type: CHEQUE_ACTIONS.FETCH_START });

        const newCheque = await chequeService.createCheque(data);

        dispatch({
          type: CHEQUE_ACTIONS.CREATE_SUCCESS,
          payload: newCheque,
        });

        showSuccess('Cheque creado correctamente');
        return newCheque;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Error al crear cheque';

        dispatch({
          type: CHEQUE_ACTIONS.FETCH_ERROR,
          payload: errorMessage,
        });

        showError(errorMessage);
        throw error;
      }
    },
    [showSuccess, showError]
  );

  /**
   * Actualizar cheque
   */
  const updateCheque = useCallback(
    async (id, data) => {
      try {
        dispatch({ type: CHEQUE_ACTIONS.FETCH_START });

        const updatedCheque = await chequeService.updateCheque(id, data);

        dispatch({
          type: CHEQUE_ACTIONS.UPDATE_SUCCESS,
          payload: updatedCheque,
        });

        showSuccess('Cheque actualizado correctamente');
        return updatedCheque;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Error al actualizar cheque';

        dispatch({
          type: CHEQUE_ACTIONS.FETCH_ERROR,
          payload: errorMessage,
        });

        showError(errorMessage);
        throw error;
      }
    },
    [showSuccess, showError]
  );

  /**
   * Eliminar cheque
   */
  const deleteCheque = useCallback(
    async (id) => {
      try {
        dispatch({ type: CHEQUE_ACTIONS.FETCH_START });

        await chequeService.deleteCheque(id);

        dispatch({
          type: CHEQUE_ACTIONS.DELETE_SUCCESS,
          payload: id,
        });

        showSuccess('Cheque eliminado correctamente');
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Error al eliminar cheque';

        dispatch({
          type: CHEQUE_ACTIONS.FETCH_ERROR,
          payload: errorMessage,
        });

        showError(errorMessage);
        throw error;
      }
    },
    [showSuccess, showError]
  );

  /**
   * Obtener cheque por ID
   */
  const getChequeById = useCallback(
    async (id) => {
      try {
        dispatch({ type: CHEQUE_ACTIONS.FETCH_START });

        const response = await chequeService.getChequeById(id);

        // El servicio ya extrae response.data.data o response.data
        // Si el backend retorna { success: true, data: cheque }, el servicio retorna solo cheque
        const cheque = response;

        dispatch({
          type: CHEQUE_ACTIONS.SET_CURRENT_CHEQUE,
          payload: cheque,
        });

        dispatch({ type: CHEQUE_ACTIONS.FETCH_SUCCESS, payload: state.cheques });

        return cheque;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Error al obtener cheque';

        dispatch({
          type: CHEQUE_ACTIONS.FETCH_ERROR,
          payload: errorMessage,
        });

        showError(errorMessage);
        throw error;
      }
    },
    [showError, state.cheques]
  );

  /**
   * Seleccionar cheques
   */
  const selectCheques = useCallback((ids) => {
    dispatch({
      type: CHEQUE_ACTIONS.SELECT_CHEQUES,
      payload: ids,
    });
  }, []);

  /**
   * Deseleccionar cheques
   */
  const deselectCheques = useCallback((ids) => {
    dispatch({
      type: CHEQUE_ACTIONS.DESELECT_CHEQUES,
      payload: ids,
    });
  }, []);

  /**
   * Limpiar selección
   */
  const clearSelection = useCallback(() => {
    dispatch({ type: CHEQUE_ACTIONS.CLEAR_SELECTION });
  }, []);

  /**
   * Establecer filtros
   */
  const setFilters = useCallback((filters) => {
    dispatch({
      type: CHEQUE_ACTIONS.SET_FILTERS,
      payload: filters,
    });
  }, []);

  /**
   * Limpiar filtros
   */
  const clearFilters = useCallback(() => {
    dispatch({ type: CHEQUE_ACTIONS.CLEAR_FILTERS });
  }, []);

  /**
   * Limpiar error
   */
  const clearError = useCallback(() => {
    dispatch({ type: CHEQUE_ACTIONS.CLEAR_ERROR });
  }, []);

  const value = {
    // Estado
    cheques: state.cheques,
    selectedCheques: state.selectedCheques,
    filters: state.filters,
    loading: state.loading,
    error: state.error,
    currentCheque: state.currentCheque,

    // Funciones
    fetchCheques,
    createCheque,
    updateCheque,
    deleteCheque,
    getChequeById,
    selectCheques,
    deselectCheques,
    clearSelection,
    setFilters,
    clearFilters,
    clearError,
  };

  return (
    <ChequeContext.Provider value={value}>{children}</ChequeContext.Provider>
  );
};

// Hook personalizado
export const useCheque = () => {
  const context = useContext(ChequeContext);
  if (context === undefined) {
    throw new Error('useCheque debe ser usado dentro de un ChequeProvider');
  }
  return context;
};

export default ChequeContext;
